import { useEffect, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';

import { FeedbackView } from './dialogViews/FeedbackView';
import { SuccessView } from './dialogViews/SuccessView';
import type { FeedbackOption } from './types';

interface FeedbackDialogProps {
  rating: 'up' | 'down' | null;
  upOptions: FeedbackOption[];
  downOptions: FeedbackOption[];
  initialSelectedOptions?: string[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onRate: (
    rating: 'up' | 'down',
    selectedOptions: string[],
    additionalComments?: string,
  ) => void;
}

export const FeedbackDialog = ({
  rating,
  upOptions,
  downOptions,
  initialSelectedOptions = [],
  isOpen,
  onOpenChange,
  onRate,
}: FeedbackDialogProps) => {
  // state
  const [selectedFeedbackOptions, setSelectedFeedbackOptions] = useState<
    string[]
  >([]);
  const [additionalComments, setAdditionalComments] = useState<string>('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  // handlers
  const handleSubmitFeedback = () => {
    if (rating) {
      onRate(rating, selectedFeedbackOptions, additionalComments);
    }

    // Show success view in the same dialog
    setShowSuccessDialog(true);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleToggleOption = (value: string) => {
    if (selectedFeedbackOptions.includes(value)) {
      setSelectedFeedbackOptions(
        selectedFeedbackOptions.filter((item) => item !== value),
      );
    } else {
      setSelectedFeedbackOptions([...selectedFeedbackOptions, value]);
    }
  };

  const handleCommentsChange = (comments: string) => {
    setAdditionalComments(comments);
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFeedbackOptions(initialSelectedOptions);
      setAdditionalComments('');
      setShowSuccessDialog(false);
    }
  }, [isOpen, initialSelectedOptions]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="vai-max-w-[538px]"
        data-testid="feedback-dialog"
      >
        {showSuccessDialog ? (
          <SuccessView />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="vai-text-xl vai-font-semibold">
                <Trans>Tell us more:</Trans>
              </DialogTitle>
            </DialogHeader>

            <FeedbackView
              rating={rating}
              upOptions={upOptions}
              downOptions={downOptions}
              selectedFeedbackOptions={selectedFeedbackOptions}
              additionalComments={additionalComments}
              onToggleOption={handleToggleOption}
              onCommentsChange={handleCommentsChange}
              onSubmit={handleSubmitFeedback}
              onCancel={handleCancel}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
